import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { BottomNav } from '../../components/BottomNav.tsx';
import { groupBy, ofKind, useCatalog, type Kind } from '../../catalog.ts';

interface Entry { level: string; years: string; maxCovers: string }
interface Own {
  drivingLicense: boolean;
  ownVehicle: boolean;
  maxTravelKm: number;
  workAuthorization: 'NOT_SET' | 'EU_OR_FRENCH' | 'PERMIT_DECLARED';
  items: { itemId: string; kind: Kind; level: string | null; years: number | null; maxCovers: number | null }[];
  completeness: { percent: number; checks: Record<string, boolean> };
}

const CHECK_LABELS: Record<string, string> = { role: 'un poste', skills: 'des compétences', languages: 'une langue', shifts: 'vos disponibilités', mobility: 'votre mobilité' };

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ fontWeight: 800 }}>{title}</div>
        {hint && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>{hint}</div>}
      </div>
      {children}
    </section>
  );
}

function Check({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13.5, lineHeight: 1.45, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span>{children}</span>
    </label>
  );
}

const ERRORS: Record<string, string> = {
  INVALID_SENIORITY: 'Indiquez votre niveau pour chaque poste choisi.',
  INVALID_LANGUAGE_LEVEL: 'Indiquez votre niveau pour chaque langue.',
  INVALID_NUMBER: 'Une valeur numérique est hors limites (années 0 à 60, couverts 1 à 2000, rayon 0 à 500 km).',
  UNKNOWN_ITEM: 'Un élément n’est plus proposé : rechargez la page.',
};

export function ProProfile() {
  const catalog = useCatalog();
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<Map<string, Entry>>(new Map());
  const [flags, setFlags] = useState({ drivingLicense: false, ownVehicle: false, maxTravelKm: '20', workAuthorization: 'NOT_SET' as Own['workAuthorization'] });
  const [completeness, setCompleteness] = useState<Own['completeness'] | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<{ profile: Own }>('/extra/pro-profile').then(({ profile }) => {
      setSelected(new Map(profile.items.map((i) => [i.itemId, { level: i.level ?? '', years: i.years?.toString() ?? '', maxCovers: i.maxCovers?.toString() ?? '' }])));
      setFlags({ drivingLicense: profile.drivingLicense, ownVehicle: profile.ownVehicle, maxTravelKm: String(profile.maxTravelKm), workAuthorization: profile.workAuthorization });
      setCompleteness(profile.completeness);
      setLoaded(true);
    });
  }, []);

  const toggle = (id: string, on: boolean, defaults: Partial<Entry> = {}) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) next.set(id, { level: '', years: '', maxCovers: '', ...defaults });
      else next.delete(id);
      return next;
    });
  const patch = (id: string, change: Partial<Entry>) => setSelected((prev) => new Map(prev).set(id, { ...prev.get(id)!, ...change }));

  const selectedRoles = useMemo(() => (catalog ? ofKind(catalog, 'ROLE').filter((r) => selected.has(r.id)) : []), [catalog, selected]);

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.put<{ profile: Own }>('/extra/pro-profile', {
        drivingLicense: flags.drivingLicense,
        ownVehicle: flags.ownVehicle,
        maxTravelKm: flags.maxTravelKm === '' ? null : Number(flags.maxTravelKm),
        workAuthorization: flags.workAuthorization,
        items: [...selected.entries()].map(([itemId, e]) => ({
          itemId,
          level: e.level || null,
          years: e.years === '' ? null : Number(e.years),
          maxCovers: e.maxCovers === '' ? null : Number(e.maxCovers),
        })),
      });
      setCompleteness(res.profile.completeness);
      setMessage({ kind: 'ok', text: 'Profil enregistré.' });
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.' });
    } finally {
      setBusy(false);
    }
  }

  if (!catalog || !loaded) {
    return (
      <div className="app-shell">
        <TopBar title="Mon profil professionnel" back />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  const list = (kind: Kind) => ofKind(catalog, kind);
  const simpleList = (kind: Kind) =>
    groupBy(list(kind), (i) => i.group).map(([group, items]) => (
      <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {group && <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{group}</div>}
        {items.map((i) => <Check key={i.id} checked={selected.has(i.id)} onChange={(on) => toggle(i.id, on)}>{i.label}</Check>)}
      </div>
    ));

  const skillIds = new Set<string>(selectedRoles.flatMap((r) => catalog.roleSkills[r.id] ?? []));
  for (const id of selected.keys()) if (catalog.items.find((i) => i.id === id && (i.kind === 'HARD_SKILL' || i.kind === 'SOFT_SKILL'))) skillIds.add(id);
  const skills = catalog.items.filter((i) => skillIds.has(i.id));

  return (
    <div className="app-shell">
      <TopBar title="Mon profil professionnel" back />
      <div className="screen-content">
        {completeness && (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}><span>Profil complété</span><span>{completeness.percent} %</span></div>
            <div style={{ height: 8, background: 'var(--bg)', borderRadius: 99 }}><div style={{ width: `${completeness.percent}%`, height: '100%', background: 'var(--success)', borderRadius: 99 }} /></div>
            {completeness.percent < 100 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                À renseigner : {Object.entries(completeness.checks).filter(([, ok]) => !ok).map(([k]) => CHECK_LABELS[k]).join(', ')}. Un profil complet est classé plus haut et candidate sans blocage.
              </div>
            )}
          </div>
        )}

        <Section title="Mes postes" hint="Cochez ceux que vous exercez, avec votre niveau. Le nombre de couverts évite de vous proposer des services trop chargés.">
          {groupBy(list('ROLE'), (i) => i.group).map(([group, roles]) => (
            <div key={group} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{group}</div>
              {roles.map((r) => {
                const entry = selected.get(r.id);
                return (
                  <div key={r.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Check checked={Boolean(entry)} onChange={(on) => toggle(r.id, on, { level: 'CONFIRME' })}>{r.label}</Check>
                    {entry && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginLeft: 24 }}>
                        <select className="input" style={{ gridColumn: '1 / -1' }} aria-label={`Niveau — ${r.label}`} value={entry.level} onChange={(e) => patch(r.id, { level: e.target.value })}>
                          {Object.entries(catalog.seniority).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                        <input className="input" type="number" min={0} max={60} placeholder="Années" aria-label={`Années — ${r.label}`} value={entry.years} onChange={(e) => patch(r.id, { years: e.target.value })} />
                        <input className="input" type="number" min={1} max={2000} placeholder="Couverts" aria-label={`Couverts max — ${r.label}`} value={entry.maxCovers} onChange={(e) => patch(r.id, { maxCovers: e.target.value })} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </Section>

        <Section title="Mes compétences" hint="Proposées selon les postes choisis.">
          {skills.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Choisissez d'abord un poste.</div>}
          {(['HARD_SKILL', 'SOFT_SKILL'] as Kind[]).map((kind) => {
            const subset = skills.filter((s) => s.kind === kind);
            return subset.length > 0 && (
              <div key={kind} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{kind === 'HARD_SKILL' ? 'Techniques' : 'Savoir-être'}</div>
                {subset.map((s) => <Check key={s.id} checked={selected.has(s.id)} onChange={(on) => toggle(s.id, on)}>{s.label}</Check>)}
              </div>
            );
          })}
        </Section>

        <Section title="Logiciels maîtrisés" hint="Un établissement qui utilise le même logiciel vous retrouve immédiatement.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>Caisse et prise de commande</div>
            {list('POS').map((i) => <Check key={i.id} checked={selected.has(i.id)} onChange={(on) => toggle(i.id, on)}>{i.label}</Check>)}
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginTop: 4 }}>Réservation</div>
            {list('RESERVATION_TOOL').map((i) => <Check key={i.id} checked={selected.has(i.id)} onChange={(on) => toggle(i.id, on)}>{i.label}</Check>)}
          </div>
        </Section>

        <Section title="Langues parlées" hint="Essentiel en zone touristique ou en hôtellerie haut de gamme.">
          {list('LANGUAGE').map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13.5 }}>{l.label}</span>
              <select className="input" style={{ width: 'auto', minWidth: 150 }} aria-label={`Niveau — ${l.label}`} value={selected.get(l.id)?.level ?? ''} onChange={(e) => (e.target.value ? (selected.has(l.id) ? patch(l.id, { level: e.target.value }) : toggle(l.id, true, { level: e.target.value })) : toggle(l.id, false))}>
                <option value="">Non parlée</option>
                {Object.entries(catalog.languageLevels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          ))}
        </Section>

        <Section title="Certifications et diplômes">{simpleList('CERTIFICATION')}</Section>
        <Section title="Équipement personnel" hint="Certains établissements exigent une tenue précise : elle peut conditionner votre candidature.">{simpleList('EQUIPMENT')}</Section>
        <Section title="Mes disponibilités" hint="Les types de services que vous acceptez.">{simpleList('SHIFT_TYPE')}</Section>

        <Section title="Mobilité">
          <Check checked={flags.drivingLicense} onChange={(v) => setFlags({ ...flags, drivingLicense: v })}>Permis B</Check>
          <Check checked={flags.ownVehicle} onChange={(v) => setFlags({ ...flags, ownVehicle: v })}>Véhicule personnel</Check>
          {simpleList('TRANSPORT')}
          <div>
            <label className="field-label" htmlFor="radius">Rayon de déplacement (km)</label>
            <input id="radius" className="input" type="number" min={0} max={500} value={flags.maxTravelKm} onChange={(e) => setFlags({ ...flags, maxTravelKm: e.target.value })} />
          </div>
        </Section>

        <Section title="Autorisation de travail" hint="Information interne à Mon Extra : elle n'est jamais montrée aux établissements.">
          {([
            ['NOT_SET', 'Je ne souhaite pas le préciser pour l’instant'],
            ['EU_OR_FRENCH', 'Je suis de nationalité française ou d’un pays de l’Union européenne'],
            ['PERMIT_DECLARED', 'J’ai un titre de séjour qui m’autorise à travailler'],
          ] as const).map(([value, label]) => (
            <label key={value} style={{ display: 'flex', gap: 8, fontSize: 13.5, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="radio" name="work-authorization" checked={flags.workAuthorization === value} onChange={() => setFlags({ ...flags, workAuthorization: value })} style={{ marginTop: 3 }} />
              {label}
            </label>
          ))}
        </Section>

        {message && <div className={message.kind === 'ok' ? 'badge badge-available' : 'error-banner'} style={{ padding: '10px 14px' }}>{message.text}</div>}
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer mon profil'}</button>
      </div>
      <BottomNav />
    </div>
  );
}

